import React from "react";
import { Box, HStack, VStack } from "@chakra-ui/react";

import { Footer, NavBar } from "@components";
import { ProtectedPage } from "./ProtectedPage";

/**
 * "Full Bleed" Layout.
 * Src: https://www.joshwcomeau.com/css/full-bleed/
 */
const COLUMN_LAYOUT_STYLES = `.global-layout-wrapper {
    width: 100%;
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr min(1080px, 100%) 1fr;
}

.global-layout-wrapper > * {
    grid-column: 2;
}

.global-layout-full-bleed {
    width: 100%;
    grid-column: 1 / 4;
}`;

const VARIANT_OPTIONS = ["centered", "column"] as const;
const ALIGN_OPTIONS = ["left", "center"] as const;

type LayoutProps = {
  variant?: (typeof VARIANT_OPTIONS)[number];
  align?: (typeof ALIGN_OPTIONS)[number];
  showNav?: boolean;
  showFooter?: boolean;
  isProtected?: boolean;
  showLoggedInText?: boolean;
};

/**
 * To fill the space between the bottom of the grid content and the footer, a
 * spacer is required to pad out that extra space. Without including this, the
 * footer hugs the grid wrapper, creating a weird floating footer effect. A
 * top spacer is added for a centered item layout for a similar reason. When
 * the grid content is greater than 100% screen height, these spacers do not
 * add to the visual layout.
 */
export function Spacer() {
  return <Box flex="1" width="100%" />;
}

/**
 * Abstract layout helper to format pages in a consistent manner.
 */
export function Layout({
  variant = "column",
  align = "left",
  showNav = true,
  showFooter = true,
  isProtected = false,
  showLoggedInText = false,
  children,
}: React.PropsWithChildren<LayoutProps>) {
  const PageProtected = isProtected ? ProtectedPage : React.Fragment;
  return (
    <PageProtected>
      <style>{COLUMN_LAYOUT_STYLES}</style>
      <VStack
        alignItems="center"
        justifyContent="flex-start"
        gap="8px"
        minH="100%"
      >
        {showNav ? <NavBar showLoggedInText={showLoggedInText} /> : undefined}
        {variant === "centered" ? <Spacer /> : undefined}
        <div className="global-layout-wrapper">
          {align === "center" ? (
            <HStack>
              <Spacer />
              {children}
              <Spacer />
            </HStack>
          ) : null}
          {align === "left" ? children : undefined}
        </div>
        <Spacer />
        {showFooter ? <Footer /> : undefined}
      </VStack>
    </PageProtected>
  );
}
